import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import { UserAvatar } from '@/components/UserAvatar';
import { useAccount } from '@/hooks/queries';
import { api } from '@/lib/api/client';
import { useT } from '@/i18n';
import { DRAWER } from '@/app/nav';
import { useBodyClass } from '@/app/providers/bodyClass';
import { useShell } from '@/app/providers/Shell';
import { useTheme } from '@/app/providers/Theme';
import { Segmented } from '@/components/ui/Controls';
import { Icon } from '@/components/ui/Icon';
import { useOpenSupport } from '@/features/profile/Support';
import { cx } from '@/lib/format';
import { NavLock } from '@/features/plans/Upgrade';
import { closeTopLayer, useLayer } from '@/lib/overlay';

export function Drawer() {
  const { pathname } = useLocation();
  const { navOpen, setNavOpen } = useShell();
  const { theme, setTheme } = useTheme();
  const openSupport = useOpenSupport();
  useBodyClass('nav-open', navOpen);
  const { t } = useT();
  useLayer('drawer', navOpen, () => setNavOpen(false));
  const { plan } = useAccount();
  // the profile is read from the shared cache; it is fetched only once the menu is opened
  const profile = useQuery({ queryKey: ['profile'], queryFn: api.profile, enabled: navOpen, staleTime: Infinity }).data;
  const ref = useRef<HTMLElement>(null);
  // closing never leaves the focus (and the browser's scroll-into-view) inside the hidden drawer
  useEffect(() => {
    if (!navOpen && ref.current?.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
  }, [navOpen]);
  return (
    <>
      <aside className="drawer" aria-label={t('a11y.menu')} ref={ref} inert={!navOpen} aria-hidden={!navOpen}>
        <div className="drawer-top">
          <Link className="drawer-brand" to="/" aria-label="Orca"><img src="/assets/brand/orca-mark.png" alt="" /></Link>
          <button className="dr-x" type="button" aria-label={t('a11y.closeMenu')} onClick={() => setNavOpen(false)}><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg></button>
        </div>
        <nav>
          {DRAWER.map(([g, pages]) => (
            <div className="dr-g" key={g}>
              <span>{t(`nav:drawer.${g}`)}</span>
              {pages.map((p) => <Link key={p.path} className={cx('dr-i', p.path === pathname && 'on')} to={p.path}><Icon name={p.icon} /><span>{p.title}</span><NavLock path={p.path} label /></Link>)}
              {g === 'account' && <>
                <button type="button" className="dr-i" onClick={() => { setNavOpen(false); openSupport(); }}><Icon name="mail" /><span>{t('nav:support')}</span></button>
                <Link className="dr-i" to="/profile#referral"><Icon name="link" /><span>{t('nav:referral')}</span></Link>
              </>}
            </div>
          ))}
        </nav>
        <div className="dr-foot">
          {/* the user — their photo or a neutral placeholder; the whale stays the brand above */}
          <Link className={cx('dr-me', pathname === '/profile' && 'on')} to="/profile">
            <UserAvatar size="md" />
            <span className="dr-me-t">
              <b>{profile?.email?.address ?? t('nav:myProfile')}</b>
              <small>{plan.name}</small>
            </span>
            <span className="dr-me-go">{t('nav:profileGo')}<Icon name="chev" /></span>
          </Link>
          <Segmented className="dr-seg" label={t('a11y.theme')} value={theme} onChange={setTheme} options={[
            { value: 'dark', label: <><Icon name="moon" />{t('a11y.dark')}</> },
            { value: 'light', label: <><Icon name="sun" />{t('a11y.light')}</> },
          ]} />
        </div>
      </aside>
      <div className="drawer-scrim" onClick={closeTopLayer} />
    </>
  );
}
